import React, { useState } from "react"
import { usePageContext } from "vike-react/usePageContext"
import { twMerge } from "tailwind-merge"
import Typography from "aldoo-ra/Typography"
import Chip from "../ui/Chip"
import ProductBox from "../ui/ProductBox"
import { FIRST_ROW } from "../ui/catalogSeed"
import Pagination from "../ui/Pagination"
import Spinner from "../ui/Spinner"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage, { useCurrency } from "../hooks/useSiteLanguage"
import { usePageTitle } from "../hooks/usePageTitle"
import { LoadAuthor, ListAuthorProducts } from "../api"
import { langFromPath, stripLang } from "../utils/contentRoutes"
import { getImageURL } from "../utils/images"
import { sanitizeText } from "../utils/pageMeta"

const AUTHOR_FIELDS = "name slug description thumbnail logo backgroundImage role bioSource photoCredit"
const PAGE_PX = "px-4 sm:px-[30px] md:px-[80px] lg:px-[160px] xl:px-[280px]"

/**
 * `/author/:slug` opens on what the author has to sell; `/author/:slug/about`
 * is the bio. The old `/products` path still lands on the products, so links
 * and bookmarks that name it keep working.
 */
function authorSlugFromPath(pathname) {
  const segments = stripLang(pathname || "/").split("/").filter(Boolean)
  return { slug: segments[1], isProducts: segments[2] !== "about" }
}

// Server-side author load: <title>, description and og:image
async function onBeforeRender(pageContext) {
  const pathname = pageContext.urlParsed?.pathname || pageContext.urlPathname || "/"
  const { slug, isProducts } = authorSlugFromPath(pathname)
  if (!slug) return {}
  try {
    // the books tab: its first page in the HTML, as ProductsTab asks for it (a visitor sees the books at once)
    const listing = isProducts
      ? ListAuthorProducts({ slug, page: 0, items_per_page: 24, language: langFromPath(pathname) || "en", sortBy: "latest" }).catch(() => null)
      : null
    const author = await Promise.race([
      LoadAuthor({ slug, language: langFromPath(pathname) || "en", fields: AUTHOR_FIELDS }),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 4000)),
    ])
    if (!author?.name || author.error) return {}
    // an author with no bio: their books, for the page's text (a crawler reads it) and its description
    const books = author.description
      ? null
      : await ListAuthorProducts({ slug, page: 0, items_per_page: 6, language: langFromPath(pathname) || "en", sortBy: "recommendationScore" }).catch(() => null)
    const booksText = books?.list?.length ? sanitizeText(books.list.map((b) => b.name).join(", "), 200) : ""
    return {
      title: `${author.name} | Aldoo`,
      description: author.description ? sanitizeText(author.description, 200) : booksText ? `${author.name}: ${booksText}` : undefined,
      thumbnail: getImageURL(author.thumbnail) || getImageURL(author.logo) || undefined,
      metadata: { ogType: "profile", canonicalUrl: `https://aldoo.com/author/${slug}` },
      storefrontAuthor: author,
      storefrontAuthorBooks: books ? { slug, list: books.list || [], count: books.count || (books.list || []).length } : null,
      storefrontAuthorList: listing ? await Promise.race([listing.then((r) => (r?.list ? { slug, result: r } : null)), new Promise((resolve) => setTimeout(() => resolve(null), 2500))]) : null,
    }
  } catch (error) {
    console.error("Storefront author onBeforeRender:", error?.message || error)
    return {}
  }
}

// a quoted, comma-joined list: „A“, „B“ и „C“
function titleList(titles, and) {
  const quoted = titles.map((title) => `„${title}“`)
  return quoted.length > 1 ? `${quoted.slice(0, -1).join(", ")} ${and} ${quoted[quoted.length - 1]}` : quoted[0] || ""
}

/**
 * The page of an author with no bio yet: what the catalog knows — how many of
 * their books Aldoo has and which — and the books themselves. The server
 * renders it (onBeforeRender loads the books), so the page has text of its own.
 */
function AuthorBooksSummary({ author, slug }) {
  const t = useT()
  const pageContext = usePageContext()
  const language = useSiteLanguage()
  const currency = useCurrency()
  const seed = pageContext.storefrontAuthorBooks?.slug === slug ? pageContext.storefrontAuthorBooks : null
  const { result } = useAsync(
    () => ListAuthorProducts({ slug, page: 0, items_per_page: 6, language, currency, sortBy: "recommendationScore" }),
    [slug, language, currency],
    { initial: seed }
  )
  const list = result?.list || []
  if (!list.length) return <Typography variant="txt_dim">
    {t`No information available for this author.`}
  </Typography>

  const count = result.count || list.length
  const titles = list.slice(0, 3).map((book) => book.name)
  const text = count > 1 ? t("%1$s is the author of %2$s books on Aldoo, including %3$s.", author.name, count, titleList(titles, t`and`)) : t("%1$s is the author of %2$s on Aldoo.", author.name, titleList(titles, t`and`))
  return (
    <div className="flex flex-col items-center gap-8" data-testid="author-books-summary">
      <Typography variant="txt" className="m-0 w-full text-center">
        {text}
      </Typography>

      <div className="grid w-full justify-center gap-1 md:gap-6 grid-cols-[repeat(auto-fit,160px)] md:grid-cols-[repeat(auto-fit,190px)]">
        {list.map((product) => (
          <ProductBox key={product.pid} product={product} placement="author_page" />
        ))}
      </div>
    </div>
  )
}

/** Where a filled bio came from, as its licence asks: Wikipedia's article, CC BY-SA. */
function BioSource({ source }) {
  const t = useT()
  if (source?.name !== "Wikipedia" || !source.url) return null
  return (
    <Typography variant="txt2_dim" data-testid="author-bio-source">
      {t`Source`}:{" "}
      <a href={source.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
        {t`Wikipedia`}
      </a>{" "}
      ({source.license || "CC BY-SA 4.0"})
    </Typography>
  )
}

/** The author's portrait, round, with its credit when it is a reused (Commons) photo. */
function Portrait({ author, size = "w-[120px] h-[120px] md:w-[150px] md:h-[150px]", credit = false }) {
  const t = useT()
  const src = getImageURL(author?.thumbnail)
  if (!src) return null
  const c = author.photoCredit
  return (
    <figure className="m-0 flex flex-col items-center gap-2">
      <img src={src} alt={author.name} className={twMerge("rounded-full object-cover object-top bg-neutral", size)} loading="lazy" />
      {credit && c?.license && (
        <figcaption className="text-[12px] text-text_secondary text-center max-w-[320px]">
          {t`Photo`}: {c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">{c.artist || "Wikimedia Commons"}</a> : c.artist || "Wikimedia Commons"}, {c.license}
        </figcaption>
      )}
    </figure>
  )
}

function TabChip({ label, to, active }) {
  return <Chip label={label} to={to} tone={active ? "dark" : "outline"} className="min-w-[90px] justify-center px-5" />
}

function ProductsTab({ slug }) {
  const t = useT()
  const pageContext = usePageContext()
  const language = useSiteLanguage()
  const currency = useCurrency()
  const [page, setPage] = useState(1)
  const [contentType, setContentType] = useState(null)
  // the first page the server rendered (onBeforeRender), for this author
  const seed = pageContext.storefrontAuthorList?.slug === slug ? pageContext.storefrontAuthorList.result : null

  const { result, pending } = useAsync(
    () => ListAuthorProducts({ slug, page: page - 1, items_per_page: 24, language, currency, sortBy: "latest", contentType }),
    [slug, page, language, currency, contentType],
    { initial: page === 1 && !contentType ? seed : null }
  )

  const selectType = (type) => {
    setContentType(type)
    setPage(1)
  }

  if (!result) return pending ? <Spinner className="py-16" /> : null
  const total = (result.contentTypes || []).reduce((sum, ct) => sum + (ct.count || 0), 0)
  const typeChip = (label, count, type, icon) => {
    const active = contentType === type
    return (
      <Chip
        key={type ?? "all"}
        tone={active ? "primary" : "outline"}
        onClick={() => selectType(type)}
        className="min-w-[110px] justify-between gap-2"
        icon={icon}
        label={
          <>
            <span>{label}</span>
            <span className="text-[12px] font-bold">{count}</span>
          </>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-8">
      {/* the type filter only when there is something to choose between:
          with one type "All" and the type would do the same */}
      {result.contentTypes?.length > 1 && (
        <div className="flex flex-row flex-wrap items-center gap-2 border-b border-bordered pb-4">
          {typeChip(t`All`, total, null)}
          {result.contentTypes.map((ct) => typeChip(ct.name || ct.type, ct.count, ct.type))}
        </div>
      )}
      {result.list?.length > 0 ? (
        <>
          {/* columns of the card's width (Variants/ProductBox), as many as fit, the
              block centred; the last row stays aligned with the ones above */}
          <div className="grid w-full justify-center gap-1 md:gap-6 grid-cols-[repeat(auto-fit,160px)] md:grid-cols-[repeat(auto-fit,190px)] lg:grid-cols-[repeat(auto-fit,232px)]">
            {result.list.map((product, i) => (
              <ProductBox key={product.pid} product={product} placement="author_page" priority={page === 1 && i < FIRST_ROW} />
            ))}
          </div>
          {result.pages > 1 && <Pagination page={page} count={result.pages} onChange={setPage} className="mt-4" />}
        </>
      ) : (
        <Typography variant="txt_dim">
          {t`No products available from this author.`}
        </Typography>
      )}
    </div>
  )
}

/**
 * /author/:slug (the products) and /author/:slug/about (the bio) — hero with
 * background, portrait and name; tabs; the paged product grid, or logo + bio.
 */
export default function AuthorPage() {
  const t = useT()
  const pageContext = usePageContext()
  const language = useSiteLanguage()
  const { slug, isProducts } = authorSlugFromPath(pageContext.urlPathname)
  const ssr = pageContext.storefrontAuthor
  const initial = ssr?.slug === slug ? ssr : null

  const { result: loaded, pending } = useAsync(
    () => LoadAuthor({ slug, language, fields: AUTHOR_FIELDS }),
    [slug, language],
    { initial, enabled: !!slug }
  )
  const author = loaded?.slug === slug ? loaded : loaded && !loaded.error && !initial ? loaded : initial || (loaded?.error ? null : loaded)

  usePageTitle(author?.name)

  if (!slug) return <div className="py-32 text-center text-text_secondary">{t`No information available for this author.`}</div>
  if (!author) return pending ? <Spinner className="py-32" /> : <div className="py-32 text-center text-text_secondary">{t`No information available for this author.`}</div>

  return (
    <div className="flex w-full flex-col items-center">
      {/* Header: the author's portrait (when there is one) and name, centred */}
      <div className={twMerge("w-full border-b border-bordered py-2 flex flex-col items-center gap-3", PAGE_PX)}>
        {isProducts && <Portrait author={author} size="w-[72px] h-[72px] md:w-[88px] md:h-[88px] mt-3" />}
        <Typography variant="h1" className="text-center">
          {author.name}
        </Typography>
      </div>

      {/* Tabs */}
      <div className={twMerge("w-full pt-3 mb-5 md:mb-[50px] lg:mb-[70px]", PAGE_PX)}>
        <div className="mx-auto flex max-w-[1360px] flex-row justify-center gap-4">
          <TabChip label={t`Products`} to={`/author/${slug}`} active={isProducts} />
          <TabChip label={t`About`} to={`/author/${slug}/about`} active={!isProducts} />
        </div>
      </div>

      <div className={twMerge("w-full pb-10 md:pb-20", PAGE_PX)}>
        {isProducts ? (
          <ProductsTab slug={slug} />
        ) : (
          // the bio in one centred column, at a reading width
          <div className="mx-auto flex w-full max-w-[760px] flex-col items-center gap-5">
            <Portrait author={author} credit />
            {author.description ? (
              <>
                <div className="sf-html w-full text-[16px] leading-[1.6]" dangerouslySetInnerHTML={{ __html: author.description }} />
                <div className="w-full">
                  <BioSource source={author.bioSource} />
                </div>
              </>
            ) : (
              <AuthorBooksSummary author={author} slug={slug} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

AuthorPage.onBeforeRender = onBeforeRender
